import {array} from 'json-bouncer'
import * as R from 'ramda'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'
import {
    mandatoryNullableValidator,
    validateInputField,
    ValidationOutcome
} from '../../../Data/FormValidation'
import {decodeHttpResponse} from '../../../Data/Http'
import * as Nullable from '../../../Data/Nullable'
import {update} from '../../../Data/ObjectHelper'
import * as Result from '../../../Data/Result'
import * as WebData from '../../../Data/WebData'
import * as BpsUser from '../../../Entities/BpsUser'
import * as Company from '../../../Entities/Company'
import {Customer, customerDecoder} from '../../../Entities/Customer'
import * as BpsUserResource from '../../../Request/BpsUserResource'
import * as CompanyResource from '../../../Request/CompanyResource'
import * as CustomerResource from '../../../Request/CustomerResource'
import {OrderCreateFeedback} from '../OrderCreateFeedback'
import {Step1SubAction} from './Step1Actions'
import {Step1Data, Step1State} from './Step1State'

type Step1Loop = FeedbackLoop<Step1State, OrderCreateFeedback>

export function initStep1(step1: Step1Data | null): Step1Loop {
    return {
        state: {
            customers: {
                type: 'Loading'
            },
            companySearch: step1 !== null ? Company.label(step1.company) : '',
            companySuggestions: [],
            bpsUserSearch: step1 !== null ? BpsUser.label(step1.bpsUser) : '',
            bpsUserSuggestions: [],
            input:
                step1 !== null ?
                    step1 :
                    {
                        transactionNumber: '',
                        customer: null,
                        company: null,
                        bpsUser: null,
                    },
            validation: {
                customer: null,
                company: null,
                bpsUser: null,
            },
            submissionAttempted: false,
        },
        feedback: [{
            type: 'SendRequest',
            identifier: {
                type: 'CustomerList'
            },
            httpRequest: CustomerResource.list(),
        }]
    }
}

export function step1Reducer(
    state: Step1State,
    action: Step1SubAction
): Step1Loop {
    switch (action.type) {
        case 'TransactionNumberChanged':
            return {
                state: update({
                    input: update({
                        transactionNumber: action.value,
                    }, state.input),
                }, state),
                feedback: [],
            }
        case 'CustomerIdChanged': {
            const selectedCustomer =
                Nullable.andThen(
                    customerId =>
                        Nullable.andThen(
                            customers =>
                                Nullable.fromPossiblyUndefined(
                                    R.find(
                                        customer => customer.id === customerId,
                                        customers)),
                            WebData.toNullable(state.customers)),
                    action.value)
            return {
                state: update({
                    companySearch: '',
                    bpsUserSearch: '',
                    input: update({
                        customer: selectedCustomer,
                        company: null,
                        bpsUser: null,
                    }, state.input),
                    validation: update({
                        customer: validateInputField<Customer | null, 'Mandatory', Customer>(
                            validators.customer,
                            state.submissionAttempted,
                            selectedCustomer),
                        company: validateInputField<Company.Company | null, 'Mandatory', Company.Company>(
                            validators.company,
                            state.submissionAttempted,
                            null),
                        bpsUser: validateInputField<Customer | null, 'Mandatory', Customer>(
                            validators.bpsUser,
                            state.submissionAttempted,
                            null),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        }
        case 'ListCustomersResponse': {
            const customers = WebData.fromResult(
                decodeHttpResponse(array(
                    customerDecoder()),
                    action.result))
            return {
                state: update({
                    customers,
                }, state),
                feedback: [],
            }
        }
        case 'CompanySearchChanged':
            return {
                state: update({
                    companySearch: action.value,
                    bpsUserSearch: '',
                    input: update({
                        company: null,
                        bpsUser: null,
                    }, state.input),
                    validation: update({
                        company: validateInputField<Company.Company | null, 'Mandatory', Company.Company>(
                            validators.company,
                            state.submissionAttempted,
                            null),
                        bpsUser: validateInputField<BpsUser.BpsUser | null, 'Mandatory', BpsUser.BpsUser>(
                            validators.bpsUser,
                            state.submissionAttempted,
                            null),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        case 'FetchCompanySuggestions':
            return {
                state,
                feedback:
                    state.input.customer !== null ?
                        [{
                            type: 'SendRequest',
                            identifier: {
                                type: 'CompanySuggestionList'
                            },
                            httpRequest:
                                CompanyResource.listSuggestionsForCustomer(
                                    state.input.customer.id,
                                    state.companySearch,
                                ),
                        }] :
                        [],
            }
        case 'CompanySuggestionListResponse':
            const companies =
                decodeHttpResponse(array(
                    Company.companyDecoder()),
                    action.result)
            return {
                state: update({
                    companySuggestions:
                        companies.type === 'Ok' ? companies.value : [],
                }, state),
                feedback: [],
            }
        case 'ClearCompanySuggestions':
            return {
                state: update({
                    companySuggestions: [],
                }, state),
                feedback: [],
            }
        case 'CompanyChanged':
            return {
                state: update({
                    input: update({
                        company: action.value,
                        bpsUser: null,
                    }, state.input),
                    validation: update({
                        company: validateInputField<Company.Company | null, 'Mandatory', Company.Company>(
                            validators.company,
                            state.submissionAttempted,
                            action.value),
                        bpsUser: validateInputField<BpsUser.BpsUser | null, 'Mandatory', BpsUser.BpsUser>(
                            validators.bpsUser,
                            state.submissionAttempted,
                            null),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        case 'BpsUserSearchChanged':
            return {
                state: update({
                    bpsUserSearch: action.value,
                    input: update({
                        bpsUser: null,
                    }, state.input),
                    validation: update({
                        bpsUser:
                            validateInputField(
                                validators.bpsUser,
                                state.submissionAttempted,
                                action.value),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        case 'FetchBpsUserSuggestions':
            return {
                state,
                feedback:
                    state.input.company !== null ?
                        [{
                            type: 'SendRequest',
                            identifier: {
                                type: 'BpsUserSuggestionList'
                            },
                            httpRequest:
                                BpsUserResource.listSuggestionsForCompany(
                                    state.input.company.id,
                                    state.bpsUserSearch,
                                ),
                        }] :
                        [],
            }
        case 'BpsUserSuggestionListResponse':
            const bpsUsers =
                decodeHttpResponse(array(
                    BpsUser.bpsUserDecoder()),
                    action.result)
            return {
                state: update({
                    bpsUserSuggestions:
                        bpsUsers.type === 'Ok' ? bpsUsers.value : [],
                }, state),
                feedback: [],
            }
        case 'ClearBpsUserSuggestions':
            return {
                state: update({
                    bpsUserSuggestions: [],
                }, state),
                feedback: [],
            }
        case 'BpsUserChanged':
            return {
                state: update({
                    input: update(
                        {bpsUser: action.value}, state.input,
                    ),
                    validation: update(
                        {
                            bpsUser: validateInputField<BpsUser.BpsUser | null, 'Mandatory', BpsUser.BpsUser>(
                                validators.bpsUser,
                                state.submissionAttempted,
                                action.value,
                            ),
                        },
                        state.validation,
                    ),
                }, state),
                feedback: [],
            }
    }
}

const validators = {
    customer: mandatoryNullableValidator(),
    company: mandatoryNullableValidator(),
    bpsUser: mandatoryNullableValidator(),
}

export function validateStep1(state: Step1State): ValidationOutcome<Step1Data, Step1State> {
    const
        {input} = state,
        validation = {
            customer: validators.customer(input.customer),
            company: validators.company(input.company),
            bpsUser: validators.bpsUser(input.bpsUser),
        }
    if (validation.customer.type === 'Ok' &&
        validation.company.type === 'Ok' &&
        validation.bpsUser.type === 'Ok'
    ) {
        return {
            state,
            data: {
                transactionNumber: input.transactionNumber,
                customer: validation.customer.value,
                company: validation.company.value,
                bpsUser: validation.bpsUser.value,
            },
        }
    } else {
        return {
            state: update(
                {
                    validation: {
                        customer: Result.errorToNullable(validation.customer),
                        company: Result.errorToNullable(validation.company),
                        bpsUser: Result.errorToNullable(validation.bpsUser),
                    },
                },
                state,
            ),
            data: null,
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderCreate/Step1/Step1Reducer.ts